import { useCallback } from "react";

import ConfirmationModal from "shared/components/Modals/ConfirmationModal";
import useModal from "shared/hooks/useModal";

interface ConfirmOptions {
  title: string;
  message?: string;
  description?: string | string[];
  submitButtonLabel?: string;
  cancelLinkLabel?: string;
}

/**
 * Hook that provides a `confirm()` function which opens the styled ConfirmationModal.
 * Returns a promise-like callback: the onConfirm runs if the user confirms.
 *
 * Usage:
 *   const { confirm } = useConfirmation();
 *   confirm({
 *     title: "Remove Document",
 *     message: "Are you sure?",
 *     submitButtonLabel: "Remove",
 *   }, () => { deleteItem(id); });
 */
export const useConfirmation = () => {
  const { openModalDialog } = useModal();

  const confirm = useCallback(
    (options: ConfirmOptions, onConfirm: () => void) => {
      openModalDialog(ConfirmationModal, {
        ...options,
        onConfirm,
      });
    },
    [openModalDialog]
  );

  return { confirm };
};
